import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import {
  fetchYowanAdminClassApplicationList,
  fetchYowanAdminClassDetail,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const APPLICATIONS_PAGE_SIZE = 30;

const actionColumnStyle = {
  width: '10rem',
  minWidth: '10rem',
};

const scheduleColumnStyle = {
  width: '15rem',
  minWidth: '15rem',
};

const statusColumnStyle = {
  width: '8rem',
  minWidth: '8rem',
};

const appliedAtColumnStyle = {
  width: '10rem',
  minWidth: '10rem',
};

const sortApplicationRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'name-asc') {
      return left.applicantName.localeCompare(right.applicantName, 'ja');
    }

    if (sortKey === 'name-desc') {
      return right.applicantName.localeCompare(left.applicantName, 'ja');
    }

    const leftDate = Date.parse(String(left.createdAt || '').replace(' ', 'T'));
    const rightDate = Date.parse(String(right.createdAt || '').replace(' ', 'T'));
    const safeLeft = Number.isNaN(leftDate) ? 0 : leftDate;
    const safeRight = Number.isNaN(rightDate) ? 0 : rightDate;
    return safeRight - safeLeft;
  });

  return nextRows;
};

const AdminClassApplicationsListPage = () => {
  const content = yowanPageContent['/admin/events/classes/:classId/applications'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { classId } = useParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('created-desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [classRecord, setClassRecord] = useState(null);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let isMounted = true;

    setIsLoading(true);
    setLoadError('');

    Promise.all([
      fetchYowanAdminClassApplicationList({
        classId,
        page: currentPage,
        pageSize: APPLICATIONS_PAGE_SIZE,
        q: searchQuery.trim(),
      }),
      fetchYowanAdminClassDetail(classId).catch(() => null),
    ])
      .then(([payload, classDetail]) => {
        if (!isMounted) {
          return;
        }

        setRows(payload.results);
        setTotalCount(payload.count);
        setClassRecord(payload.classRecord || classDetail);
      })
      .catch(() => {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setTotalCount(0);
        setClassRecord(null);
        setLoadError('教室申込者一覧を読み込めませんでした。');
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [classId, currentPage, searchQuery]);

  if (!content) {
    return null;
  }

  const headerTitle = classRecord ? `${classRecord.title} 申込者一覧` : content.header.title;
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredRows = normalizedQuery
    ? rows.filter((row) =>
        [
          row.applicantName,
          row.applicantEmail,
          row.scheduleLabel,
          row.statusLabel,
        ]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(normalizedQuery)),
      )
    : rows;
  const sortedRows = sortApplicationRows(filteredRows, sortKey);
  const totalPages = Math.max(1, Math.ceil(totalCount / APPLICATIONS_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * APPLICATIONS_PAGE_SIZE;
  const visibleRows = sortedRows;

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={headerTitle}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="classes-list" to={appendYowanPreviewQueryIfNeeded('/admin/events/classes', preservedDomainSearch)}>
            教室一覧
          </Link>,
          classRecord ? (
            <Link key="class-edit" to={appendYowanPreviewQueryIfNeeded(`/admin/events/${classId}/edit?kind=class`, preservedDomainSearch)}>
              教室編集
            </Link>
          ) : null,
        ]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        searchPlaceholder="氏名 / メールアドレス / 開催情報 / 状態"
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        sortOptions={[
          { value: 'created-desc', label: '申込日が新しい順' },
          { value: 'name-asc', label: '氏名昇順' },
          { value: 'name-desc', label: '氏名降順' },
        ]}
      />

      {isLoading ? (
        <div className="yowan-admin-form c-surface-content">
          <LoadingSpinner label="教室申込者一覧を読み込み中" showLabel />
        </div>
      ) : null}
      {!isLoading && loadError ? (
        <p className="yowan-admin-form__message">{loadError}</p>
      ) : null}
      {!isLoading && !loadError && visibleRows.length ? (
        <div className="yowan-admin-resource-table-wrap c-surface-content">
          <table className="yowan-admin-resource-table">
            <thead>
              <tr>
                <th scope="col">#</th>
                <th scope="col">申込者</th>
                <th scope="col" style={scheduleColumnStyle}>開催情報</th>
                <th scope="col" style={statusColumnStyle}>状態</th>
                <th scope="col" style={appliedAtColumnStyle}>申込日</th>
                <th scope="col" style={actionColumnStyle}>操作</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, index) => (
                <tr key={row.id}>
                  <td>{startIndex + index + 1}</td>
                  <td>
                    <p className="yowan-admin-resource-table__title">{row.applicantName}</p>
                    <p className="yowan-admin-resource-table__subline">{row.applicantEmail}</p>
                  </td>
                  <td style={scheduleColumnStyle}>{row.scheduleLabel || '-'}</td>
                  <td style={statusColumnStyle}>{row.statusLabel || '-'}</td>
                  <td style={appliedAtColumnStyle}>{row.createdAt || '-'}</td>
                  <td style={actionColumnStyle}>
                    <Link to={appendYowanPreviewQueryIfNeeded(`/admin/events/classes/applications/${row.id}`, preservedDomainSearch)}>
                      詳細
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {!isLoading && !loadError && !visibleRows.length ? (
        <div className="yowan-admin-form c-surface-content">
          <p className="yowan-admin-form__message">申込者はまだありません。</p>
        </div>
      ) : null}

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminClassApplicationsListPage;
